/**
 * X の共有の成果（/p/:projectId/:env/share）: 直近 30 日の訪問・新しいプレイヤー・プレイと、共有リンクごとの成果（売上は first-touch）。
 * サーバー: GET …/environments/:env/share/stats・POST …/share/links（modules/share/routes.ts・traffic.ts）。
 *
 * MASTER-PLAN「X との相性を最重要に」: クリエイターが知りたいのは「どの投稿が効いたか」。だからリンクごとの表を主にし、
 * 自分の投稿用のリンク（campaign）を作る欄を同じ画面に置く（作る → 貼る → ここで比べる）。
 * 記録（share.tracking）もリンク（share.links）も既定 OFF。オフなら「config のどのキーを true にするか」だけ出す。
 * 下に「X のカードのタグ」の欄（components/ShareTagsCard.tsx・2026-09-30 console/share-tags・founder 案 D）: 作者が自分の <head> に貼るタグ。
 *   成果の表とは別の口（…/share/tags）を読むので、成果が取れなくても欄は出す（成果の {s && …} の外に置く）。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, envPath } from '../api';
import { ago, CopyButton, Empty, ErrorState, num, PageHeader, Spinner, StatTile } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, translate, useT, type MessageKey } from '../i18n';
import { isLaunchHidden } from '../launchHidden';

/** 課金（payments）を見せない間は売上の欄（タイル・表の列）を出さない（2026-09-29 creator/launch-money・launchHidden.ts）。売上は生まれない = 常に — の欄になるため */
const SALES_HIDDEN = isLaunchHidden('payments');
import { money } from '../money';
import type { ShareStats } from '../moneyApi';
import { useShell } from './ProjectShell';
import { CLI } from '../cliCommand';
import { ShareTagsCard } from '../components/ShareTagsCard';
import { projectEyebrow } from '../projectNav';

const KIND_KEYS: Record<string, MessageKey> = {
  campaign: 'share.kind.campaign',
  plain: 'share.kind.plain',
  score: 'share.kind.score',
  rank: 'share.kind.rank',
  challenge: 'share.kind.challenge'
};
const kindLabel = (k: string) => (KIND_KEYS[k] ? translate(KIND_KEYS[k]) : k);

/** 通貨ごとに合計する（換算しない・Payments の画面と同じ方針） */
function revenueText(rows: Array<{ currency: string; amount: number }>): string {
  if (rows.length === 0) return '—';
  return rows.map((r) => money(r.amount, r.currency)).join(' + ');
}

export function SharePage() {
  const { project, env } = useShell();
  const base = envPath(project.id, env);
  const r = useResource<ShareStats>(`${base}/share/stats`);
  const { t, tr } = useT();

  const s = r.data;
  const sales = new Map<string, number>();
  for (const l of s?.byShare ?? []) for (const x of l.revenue) sales.set(x.currency, (sales.get(x.currency) ?? 0) + x.amount);

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('share.title')} />
      <p className="page-lead muted">{t(SALES_HIDDEN ? 'share.leadNoSales' : 'share.lead')}</p>
      {r.loading && !s && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('share.title')} onRetry={r.reload} />}
      {s && (
        <>
          {!s.tracking && (
            <div className="alert alert-info" role="note">
              <strong>{t('share.trackingOffTitle')}</strong>
              <span>{tr('share.trackingOffBody', { key: <code>share.tracking.enabled</code> })}</span>
              {/* config のキーだけでは手が止まる。X まわりをまとめて ON にして反映する実在のコマンドを出す（packages/cli share on） */}
              <span>{tr('share.turnOnCmd', { cmd: <code>{CLI} share on --env {env}</code> })}</span>
            </div>
          )}
          <section className="tiles" aria-label={t('share.range')}>
            <StatTile label={t('share.visits')} value={num(s.totals.visits)} hint={t('share.range')} tone="accent" />
            <StatTile label={t('share.players')} value={num(s.totals.players)} />
            <StatTile label={t('share.plays')} value={num(s.totals.plays)} />
            {!SALES_HIDDEN && <StatTile label={t('share.sales')} value={revenueText([...sales].map(([currency, amount]) => ({ currency, amount })))} />}
          </section>

          <section className="card">
            {s.byShare.length === 0 ? (
              <Empty title={t('share.emptyTitle')}>
                <p>{tr('share.emptyBody', { call: <code>kumo.share()</code> })}</p>
                <p className="small">{tr('share.emptyCli', { cmd: <code>{CLI} share link "launch post" --env {env}</code>, mcp: <code>share_link_create</code> })}</p>
              </Empty>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('share.col.link')}</th>
                      <th className="num">{t('share.col.visits')}</th>
                      <th className="num">{t('share.col.players')}</th>
                      <th className="num">{t('share.col.plays')}</th>
                      {!SALES_HIDDEN && <th className="num">{t('share.col.sales')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {s.byShare.map((l) => (
                      <tr key={l.shareId}>
                        <td>
                          <strong className="share-label">{l.label ?? (l.kind === 'campaign' ? t('share.noLabel') : kindLabel(l.kind))}</strong>
                          <span className="muted small">
                            {l.kind === 'campaign' ? kindLabel(l.kind) : null}
                            {l.kind === 'campaign' ? ' · ' : ''}
                            <span className="mono">ks={l.shareId}</span> · {ago(l.createdAt)}
                          </span>
                        </td>
                        <td className="num">{num(l.visits)}</td>
                        <td className="num">
                          <strong>{num(l.players)}</strong>
                        </td>
                        <td className="num">{num(l.plays)}</td>
                        {!SALES_HIDDEN && <td className="num">{revenueText(l.revenue)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <NewLinkCard base={base} onCreated={() => r.reload()} />
        </>
      )}
      {/* 成果（上）が先・貼る物（下）が後: この画面の目的は「どの投稿が効いたか」。タグは最初に一度貼れば済む物なので下に置く */}
      <ShareTagsCard base={base} env={env} />
    </>
  );
}

function NewLinkCard({ base, onCreated }: { base: string; onCreated: () => void }) {
  const { t, tr } = useT();
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [made, setMade] = useState<{ url: string | null; query: string } | null>(null);
  useEffect(() => {
    setMade(null);
    setErr(null);
  }, [base]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api<{ url: string | null; query: string }>(`${base}/share/links`, { method: 'POST', body: { label: label.trim() } });
      setMade(res);
      setLabel('');
      onCreated();
    } catch (e2) {
      setErr(e2 as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card share-new" onSubmit={submit}>
      <h2 className="card-title">{t('share.newLink')}</h2>
      <p className="small muted">{t('share.newLinkLead')}</p>
      <div className="form-inline">
        <label className="field field-grow">
          <span>{t('share.label')}</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} placeholder={t('share.labelPlaceholder')} required />
        </label>
        <button className="btn btn-primary" disabled={busy || !label.trim()}>
          {busy ? t('common.working') : t('share.create')}
        </button>
      </div>
      {err &&
        (err.code === 'share_links_disabled' ? (
          <p className="alert alert-info" role="note">
            {tr('share.linksOff', { key: <code>share.links.enabled</code> })}
          </p>
        ) : (
          <p className="alert alert-error" role="alert">
            {errorText(err)}
          </p>
        ))}
      {made && (
        <div className="alert alert-ok" role="status">
          {made.url ? (
            <>
              <span>{t('share.created')}</span>
              <span className="callback-line">
                <code>{made.url}</code>
                <CopyButton text={made.url} />
              </span>
            </>
          ) : (
            <span>{tr('share.createdQuery', { query: <code>?{made.query}</code> })}</span>
          )}
        </div>
      )}
    </form>
  );
}
